import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, ChevronDown, Check } from "lucide-react";
import { useCreateExport } from "../lib/queries";
import { useDateRange } from "../lib/dateRange";

const FORMATS: { fmt: string; label: string }[] = [
  { fmt: "csv", label: "CSV" },
  { fmt: "xlsx", label: "Excel (XLSX)" },
  { fmt: "pdf", label: "PDF report" },
  { fmt: "json", label: "JSON" },
];

/**
 * Universal bulk-export control. Respects the active date range + any extra
 * filters (severity, site, search…). The export is generated async (Celery)
 * and appears in Export History; every export is audit-stamped.
 */
export default function ExportMenu({
  title = "Export",
  extraFilters = {},
}: {
  title?: string;
  extraFilters?: Record<string, unknown>;
}) {
  const { params } = useDateRange();
  const create = useCreateExport();
  const [open, setOpen] = useState(false);
  const [queued, setQueued] = useState(false);

  function run(fmt: string) {
    create.mutate(
      { kind: "bulk", fmt, title, filters: { ...params, ...extraFilters } },
      { onSuccess: () => { setQueued(true); setTimeout(() => setQueued(false), 6000); } }
    );
    setOpen(false);
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-ink transition hover:border-aurora-cyan/40"
      >
        <Download size={15} /> {title}
        <ChevronDown size={13} className="text-slatey" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="glass absolute right-0 top-10 z-40 w-44 p-1">
            {FORMATS.map((f) => (
              <button
                key={f.fmt}
                onClick={() => run(f.fmt)}
                className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-sm text-slate-300 hover:bg-white/5 hover:text-ink"
              >
                {f.label}
                <span className="mono text-[10px] uppercase text-slatey">{f.fmt}</span>
              </button>
            ))}
          </div>
        </>
      )}
      {queued && (
        <div className="absolute right-0 top-11 z-20 flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-aurora-teal/40 bg-aurora-teal/10 px-3 py-1.5 text-xs text-aurora-teal">
          <Check size={13} /> Queued · <Link to="/exports" className="underline">Export History</Link>
        </div>
      )}
    </div>
  );
}
